"use client";

import { useEffect, useMemo, useState } from "react";
import { Download, Copy, Share2, Check, Trophy, Smartphone, User, Users, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { supabaseHost } from "@/app/_module/utils/imageTransform";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/app/_module/services/axiosConfig";

/**
 * Share-your-win dialog — leaderboard entry point.
 *
 * Rebuilt (2026-08-11) to mirror the team-chat sales-win share the user called
 * "perfect", and modeled on the IG posts agencies actually make (reference:
 * @thefoundationfinancial's weekly top-producers podium). The old 35-template
 * picker is gone — two cards, both in the sales-win design system:
 *  - every agent: `rankwin` — their rank + production, full-height photo panel
 *  - the agency owner: `topboard` — the top-3 podium with headshots (IG 4:5)
 *
 * The image renders server-side at /api/share-card; this dialog assembles the
 * URL (photos travel as bare bucket paths, never full supabase URLs) and hands
 * the PNG to the OS: native share sheet on phones, download on desktop, QR
 * hand-off to /share for the desktop→phone→Instagram road.
 */

export interface ShareMe {
  name: string;
  rank: number;
  /** Policies/families count for the period — compliance-safe by default. */
  count: number;
  /** Formatted premium-sold figure (e.g. "$30,430") — powers the money line. */
  premiumSold?: string;
  agencyName?: string;
  /** Full supabase URL — reduced to a bare bucket path before it hits the URL. */
  photoUrl?: string;
}

export interface ShareTop3 {
  agencyName: string;
  /** Up to 10 rows — podium card uses 3, the top-ten grid all of them. */
  rows: Array<{
    name: string;
    value: string;
    /** Policy count for the plaque's second line. */
    count?: number;
    photoUrl?: string;
  }>;
  /** Whole-board team total for the top-ten mid-band flex. */
  totalPremium?: number;
  /** Number of producing agents on the board ("72 WRITERS"). */
  writers?: number;
}


/** Storage URLs travel as bare bucket paths when they live on THIS project —
 * the share link is user-visible (hover, copy, QR) and must not expose the
 * supabase host. A URL on our OTHER pinned project (dev DB rows sometimes hold
 * prod-hosted assets, e.g. Tony Stark's photo) is passed through WHOLE: the
 * route's legacy branch only accepts our exact hosts, and rebuilding a foreign
 * path against this env's origin 404s into an initials fallback. Anything else
 * is dropped entirely. */
const storagePath = (u?: string | null): string | null => {
  const m = (u || "").match(
    /^https:\/\/([a-z0-9-]+\.supabase\.co)\/storage\/v1\/(?:object|render\/image)\/public\/([^?]+)/
  );
  if (!m) return null;
  return m[1] === supabaseHost() ? decodeURIComponent(m[2]) : (u as string);
};

/** Strip "$1,234" → "1234" for the API's numeric params. */
const digits = (v?: string | null) => (v || "").replace(/[^0-9]/g, "");

// ── Legacy gallery ("More styles") ──────────────────────────────────────────
// The 46 original templates predating rankwin/topboard. They still render at
// /api/share-card under their old params — this table is just the picker.
type TplGroup = "wins" | "money" | "streaks" | "awards" | "career" | "seasonal" | "team";

const TEMPLATE_GROUPS: Array<{ id: TplGroup; label: string; ownerOnly?: boolean; needsMoney?: boolean }> = [
  { id: "wins", label: "Wins" },
  { id: "money", label: "Money", needsMoney: true },
  { id: "streaks", label: "Streaks" },
  { id: "awards", label: "Awards" },
  { id: "career", label: "Career" },
  { id: "seasonal", label: "Seasonal" },
  { id: "team", label: "My team", ownerOnly: true },
];

const TEMPLATES: Array<{ id: string; label: string; group: TplGroup }> = [
  { id: "milestone", label: "Classic", group: "wins" },
  { id: "allstar", label: "All-Star", group: "wins" },
  { id: "swiss", label: "Swiss", group: "wins" },
  { id: "ledger", label: "Front Page", group: "wins" },
  { id: "polaroid", label: "Polaroid", group: "wins" },
  { id: "marquee", label: "Marquee", group: "wins" },
  { id: "varsity", label: "Varsity", group: "wins" },
  { id: "passport", label: "Passport", group: "wins" },
  { id: "ticker", label: "Ticker", group: "money" },
  { id: "gold", label: "Gold", group: "money" },
  { id: "receipt", label: "Receipt", group: "money" },
  { id: "paid", label: "Issued. Paid.", group: "money" },
  { id: "bank", label: "Bank Statement", group: "money" },
  { id: "jackpot", label: "Jackpot", group: "money" },
  { id: "soldout", label: "Sold Out", group: "money" },
  { id: "invoice", label: "Invoice: PAID", group: "money" },
  { id: "certificate", label: "Certificate", group: "money" },
  { id: "moneyheat", label: "Heatmap", group: "money" },
  { id: "volt", label: "Volt", group: "streaks" },
  { id: "chain", label: "The Chain", group: "streaks" },
  { id: "trajectory", label: "Trajectory", group: "streaks" },
  { id: "domino", label: "Domino", group: "streaks" },
  { id: "odometer", label: "Odometer", group: "streaks" },
  { id: "progressring", label: "Goal Hit", group: "streaks" },
  { id: "heater", label: "Heater", group: "streaks" },
  { id: "momentum", label: "Momentum", group: "streaks" },
  { id: "medal", label: "The Medal", group: "awards" },
  { id: "ringbox", label: "Ring Box", group: "awards" },
  { id: "andstill", label: "And Still...", group: "awards" },
  { id: "halloffame", label: "Hall of Fame", group: "awards" },
  { id: "reportcard", label: "Report Card", group: "awards" },
  { id: "checkmate", label: "Checkmate", group: "awards" },
  { id: "trip", label: "Boarding Pass", group: "awards" },
  { id: "hundred", label: "The 100th", group: "career" },
  { id: "firstclose", label: "First Close", group: "career" },
  { id: "licenseday", label: "License Day", group: "career" },
  { id: "careerodometer", label: "Career Total", group: "career" },
  { id: "yearone", label: "Year One", group: "career" },
  { id: "shelf", label: "The Shelf", group: "career" },
  { id: "yearend", label: "Year-End Gold", group: "seasonal" },
  { id: "playoffs", label: "Q4 Playoffs", group: "seasonal" },
  { id: "ornament", label: "Ornament", group: "seasonal" },
  { id: "chapter", label: "New Chapter", group: "seasonal" },
  { id: "summerheat", label: "Summer Heat", group: "seasonal" },
  { id: "liam", label: "LIAM Sept", group: "seasonal" },
  { id: "wrapped", label: "Wrapped", group: "seasonal" },
  { id: "top3", label: "Hall of Champions", group: "team" },
  { id: "podium", label: "Podium", group: "team" },
  { id: "agentmonth", label: "Agent of the Month", group: "team" },
  { id: "teamgrid", label: "Team Grid", group: "team" },
  { id: "orgmilestone", label: "Team Milestone", group: "team" },
  { id: "draftpick", label: "Draft Pick", group: "team" },
  { id: "versus", label: "Versus", group: "team" },
  { id: "madness", label: "Madness Bracket", group: "team" },
];

const MONEY_IDS = new Set(TEMPLATES.filter((t) => t.group === "money").map((t) => t.id));
const TEAM_IDS = new Set(TEMPLATES.filter((t) => t.group === "team").map((t) => t.id));

export function ShareWinDialog({
  open,
  onOpenChange,
  me,
  top3,
  periodLabel,
  agencyId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  me: ShareMe | null;
  top3: ShareTop3 | null;
  periodLabel: string;
  /** The board being viewed — its own logo brands the card top (team-chat chain). */
  agencyId?: string;
}) {
  const [mode, setMode] = useState<"me" | "team" | "topten" | "styles">("me");
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { branding, isWhiteLabeled } = useBranding();

  // Legacy gallery selection.
  const [group, setGroup] = useState<TplGroup>("wins");
  const [template, setTemplate] = useState<string>("milestone");
  // Personal-card style: brand-color classic, or the dark IG-system looks.
  // ids beyond "classic" are the /api/share-card template names verbatim.
  const [meStyle, setMeStyle] = useState<string>("classic");
  const ME_STYLES: Array<{ id: string; label: string }> = [
    { id: "classic", label: "Classic" },
    { id: "spotlight", label: "Spotlight" },
    { id: "statline", label: "Stat line" },
    { id: "splitcard", label: "Split" },
    { id: "billboard", label: "Billboard" },
    { id: "jersey", label: "Jersey" },
    { id: "climbing", label: "Climbing" },
    { id: "boxscore", label: "Box score" },
    { id: "weekrecap", label: "In numbers" },
    { id: "protector", label: "Families" },
    { id: "receiptdark", label: "Receipts" },
    { id: "namemark", label: "Namemark" },
    { id: "titlecard", label: "Title card" },
    { id: "wantedposter", label: "Wanted" },
    { id: "certified", label: "Certified" },
    { id: "blueprint", label: "Blueprint" },
    { id: "shiftcard", label: "Clocked in" },
  ];
  // Team-card style for the Podium tab (topboard stays the headliner).
  const [teamStyle, setTeamStyle] = useState<string>("topboard");
  const TEAM_STYLES: Array<{ id: string; label: string }> = [
    { id: "topboard", label: "Podium" },
    { id: "onetwo", label: "One-two punch" },
    { id: "startingfive", label: "Starting five" },
    { id: "nextup", label: "Next up" },
    { id: "thewall", label: "The wall" },
    { id: "scoreboard", label: "Scoreboard" },
    { id: "recordbook", label: "For the record" },
    { id: "familycount", label: "Families" },
  ];

  // Owners without their own row jump straight to the team card.
  useEffect(() => {
    if (open) setMode(me ? "me" : "team");
  }, [open, me]);

  // Licensedproducer link — when connected with an ACTIVE listing, the card
  // carries their verified listing URL. Otherwise cards carry no link at all.
  const { data: lpStatus } = useQuery({
    queryKey: ["licensedproducer-status"],
    queryFn: async () => {
      const r = await axiosInstance.get("/api/auth/licensedproducer-status");
      return (r?.data ?? { linked: false }) as { linked: boolean; slug?: string | null };
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const lpUrl = lpStatus?.slug ? `licensedproducer.com/agents/${lpStatus.slug}` : "";

  const galleryGroups = useMemo(
    () =>
      TEMPLATE_GROUPS.filter(
        (g) => (!g.ownerOnly || !!top3) && (!g.needsMoney || !!me?.premiumSold),
      ),
    [top3, me?.premiumSold],
  );
  const galleryTemplates = useMemo(() => TEMPLATES.filter((t) => t.group === group), [group]);

  // Keep the gallery selection inside the visible group.
  useEffect(() => {
    if (!galleryTemplates.some((t) => t.id === template) && galleryTemplates[0]) {
      setTemplate(galleryTemplates[0].id);
    }
  }, [galleryTemplates, template]);

  // White-label brand carries onto the card: color paints the rankwin ground /
  // topboard accents, logo becomes the watermark. Plain InsuraCentral agencies
  // fall back to the platform look server-side.
  const brandColor = /^#[0-9a-fA-F]{6}$/.test(branding?.primaryColor || "")
    ? (branding?.primaryColor as string)
    : null;
  const brandLogoPath = isWhiteLabeled ? storagePath(branding?.logoUrl) : null;

  // The agency's OWN uploaded logo — same fallback chain the team-chat sales-win
  // card uses: agency logo → white-label brand logo → InsuraCentral mark (the
  // route's default). ⚠️ There is NO GET /agencies/:id on the backend (the
  // endpoints.ts entry 404s) — the list endpoint's items DO carry logoUrl, so we
  // fetch the list and pick the viewed agency out of it. Any failure just falls
  // down the chain.
  const { data: agencyLogoUrl } = useQuery({
    queryKey: ["share-agency-logo", agencyId],
    queryFn: async () => {
      // api + getApiUrl, NOT axiosInstance: axiosInstance's baseURL is the bare
      // backend origin without /api, so a naked "/agencies" 404s silently.
      const r = await api.get<{ data?: { items?: unknown[] }; items?: unknown[] }>({
        url: getApiUrl("/agencies"),
        auth: true,
      });
      const env = (r as { data?: unknown })?.data ?? r;
      const e = env as { data?: { items?: unknown[] }; items?: unknown[] };
      const items = (e?.data?.items ?? e?.items ?? []) as Array<{ id: string; logoUrl?: string; logo_url?: string }>;
      const mine = items.find((a) => a.id === agencyId);
      return mine?.logoUrl || mine?.logo_url || null;
    },
    enabled: open && !!agencyId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const agencyLogoPath = storagePath(agencyLogoUrl);
  const alogoPath = agencyLogoPath || brandLogoPath;

  const imgPath = useMemo(() => {
    const q = new URLSearchParams();
    // v busts the route's 1h render cache when these templates change visually.
    q.set("v", "1");
    if (lpUrl) q.set("lp", lpUrl);
    if (mode === "styles") {
      // Legacy gallery — the original param wiring, untouched server-side.
      q.set("t", template);
      q.set("kicker", periodLabel);
      if (TEAM_IDS.has(template) && top3) {
        q.set("agency", top3.agencyName);
        top3.rows.slice(0, 3).forEach((r, i) => {
          q.set(`p${i + 1}`, r.name);
          q.set(`v${i + 1}`, r.value);
        });
        if (template === "top3" || template === "podium") {
          q.set("label", "Total premium sold");
        } else if (template === "agentmonth" || template === "draftpick") {
          q.set("name", top3.rows[0]?.name || "");
          q.set("n", top3.rows[0]?.value || "");
          q.set("label", "premium sold");
        } else {
          q.set("name", me?.name || top3.rows[0]?.name || "");
          q.set("n", top3.rows[0]?.value || "");
          q.set("label", "premium sold");
        }
      } else if (me) {
        q.set("name", me.name);
        q.set("rank", String(me.rank));
        if (me.agencyName) q.set("agency", me.agencyName);
        if (MONEY_IDS.has(template) && me.premiumSold) {
          q.set("n", me.premiumSold);
          q.set("prem", me.premiumSold);
          q.set("pol", String(me.count));
          q.set("label", template === "paid" ? "issued paid premium this period" : "Premium sold");
          if (template === "gold") q.set("kicker", "Record Month");
        } else {
          q.set("n", String(me.count));
          q.set("label", "families protected");
          if (me.premiumSold) {
            q.set("prem", me.premiumSold);
            q.set("pol", String(me.count));
          }
        }
        if (template === "allstar") q.set("kicker", `Top Closer - ${periodLabel}`);
      }
      return `/api/share-card?${q.toString()}`;
    }
    if (mode === "team" && top3) {
      q.set("t", teamStyle);
      q.set("agency", top3.agencyName);
      q.set("period", periodLabel);
      // Send the whole board — each team template takes the slice it needs.
      top3.rows.slice(0, 10).forEach((r, i) => {
        q.set(`a${i + 1}`, r.name);
        const m = digits(r.value);
        if (m) q.set(`m${i + 1}`, m);
        if (r.count != null && i < 3) q.set(`c${i + 1}`, String(r.count));
        const f = storagePath(r.photoUrl);
        if (f) q.set(`f${i + 1}`, f);
      });
      if (top3.totalPremium) q.set("total", String(top3.totalPremium));
      if (top3.writers) q.set("writers", String(top3.writers));
      // Families-led cards want the team's policy count, not premium.
      const totalPolicies = top3.rows.reduce((s, r) => s + (r.count || 0), 0);
      if (totalPolicies) q.set("cnt", String(totalPolicies));
    } else if (mode === "topten" && top3) {
      q.set("t", "topten");
      q.set("agency", top3.agencyName);
      q.set("period", periodLabel);
      if (top3.totalPremium) q.set("total", String(top3.totalPremium));
      if (top3.writers) q.set("writers", String(top3.writers));
      top3.rows.slice(0, 10).forEach((r, i) => {
        q.set(`a${i + 1}`, r.name);
        const m = digits(r.value);
        if (m) q.set(`m${i + 1}`, m);
        const f = storagePath(r.photoUrl);
        if (f) q.set(`f${i + 1}`, f);
      });
    } else if (me) {
      q.set("t", meStyle === "classic" ? "rankwin" : meStyle);
      q.set("name", me.name);
      q.set("rank", String(me.rank));
      q.set("period", periodLabel);
      if (me.agencyName) q.set("agency", me.agencyName);
      if (me.count) q.set("cnt", String(me.count));
      const prem = digits(me.premiumSold);
      if (prem) q.set("prem", prem);
      const photo = storagePath(me.photoUrl);
      if (photo) q.set("photo", photo);
    }
    if (brandColor) q.set("color", brandColor);
    if (alogoPath) q.set("alogo", alogoPath);
    return `/api/share-card?${q.toString()}`;
  }, [mode, me, top3, periodLabel, brandColor, alogoPath, template, lpUrl, meStyle, teamStyle]);

  const caption = useMemo(() => {
    if (mode === "styles") {
      if (template === "agentmonth" && top3?.rows[0])
        return `Agent of the Month: ${top3.rows[0].name}. Earned, not given. #lifeinsurance #agentofthemonth`;
      if (TEAM_IDS.has(template) && top3)
        return `Hall of champions at ${top3.agencyName}. Proud of this team. #lifeinsurance #issuedpaid`;
      if (MONEY_IDS.has(template) && me?.premiumSold)
        return `${me.premiumSold} in premium sold this period. Heads down, families protected. #lifeinsurance #issuedpaid`;
      if (me)
        return `${me.count} families protected this period. Grateful for every one of them. #lifeinsurance`;
      return "";
    }
    if (mode === "team" && top3) {
      const lines = top3.rows
        .slice(0, 3)
        .map((r, i) => `${["🥇", "🥈", "🥉"][i]} ${r.name} — ${r.value}`)
        .join("\n");
      return `Top producers at ${top3.agencyName} (${periodLabel.toLowerCase()}):\n${lines}\nProud of this team. 🏆 #lifeinsurance #topproducers`;
    }
    if (mode === "topten" && top3) {
      const total = top3.totalPremium ? `$${top3.totalPremium.toLocaleString()}` : "";
      return `${periodLabel} closes at ${total || "a new high"} in issued premium across ${top3.writers || top3.rows.length} writers at ${top3.agencyName}. Discipline, preparation, execution. The board doesn't lie. 🏆 #lifeinsurance #topproducers`;
    }
    if (me) {
      return `🏆 #${me.rank} on the board ${periodLabel.toLowerCase()}${me.premiumSold ? ` — ${me.premiumSold} in issued premium` : ""}${me.count ? `, ${me.count} families protected` : ""}. Heads down. 📈 #lifeinsurance`;
    }
    return "";
  }, [mode, me, top3, periodLabel, template]);

  // The QR must encode an ABSOLUTE url — it's scanned by a camera, not our app.
  const [origin, setOrigin] = useState("");
  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);
  const handoffUrl = origin
    ? `${origin}/share?img=${encodeURIComponent(imgPath)}&cap=${encodeURIComponent(caption)}`
    : "";
  const qrSrc = handoffUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(handoffUrl)}`
    : "";

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard denied — the caption is visible to select manually */
    }
  };

  // Native share sheet is a PHONE flow; desktop Share just downloads (same
  // decision as SalesWinShareDialog — macOS share sheets dead-end).
  const isMobile =
    typeof navigator !== "undefined" &&
    (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 2 && /Mac/.test(navigator.userAgent)));

  const nativeShare = async () => {
    setSharing(true);
    try {
      const blob = await (await fetch(imgPath)).blob();
      const file = new File([blob], "insuracentral-win.png", { type: "image/png" });
      if (isMobile && navigator.canShare?.({ files: [file] })) {
        try { await navigator.clipboard.writeText(caption); } catch { /* non-fatal */ }
        // Files only — iOS shares the title INSTEAD of the image otherwise.
        await navigator.share({ files: [file] });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "insuracentral-win.png";
        a.click();
        URL.revokeObjectURL(a.href);
      }
    } catch {
      /* user closed the sheet */
    } finally {
      setSharing(false);
    }
  };

  const canTeam = !!top3;
  const canMe = !!me;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            Share your stats
          </DialogTitle>
          <DialogDescription>
            Your card is ready — post it, story it, send it.
          </DialogDescription>
        </DialogHeader>

        {/* Card switcher: the two hero cards up front, the legacy gallery behind
            "More styles" so 46 older designs stay reachable without cluttering
            the default flow. */}
        <div className="flex items-center gap-1 self-start rounded-lg bg-muted p-1">
          {canMe && (
            <button
              type="button"
              onClick={() => setMode("me")}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${mode === "me" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              <User className="h-3.5 w-3.5" /> My rank
            </button>
          )}
          {canTeam && (
            <button
              type="button"
              onClick={() => setMode("team")}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${mode === "team" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Users className="h-3.5 w-3.5" /> Podium
            </button>
          )}
          {/* >1 row, not >3: a 2-3 person board still renders a fine top-ten
              grid, and hiding the tab made owners think the feature was missing. */}
          {canTeam && (top3?.rows.length ?? 0) > 1 && (
            <button
              type="button"
              onClick={() => setMode("topten")}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${mode === "topten" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Trophy className="h-3.5 w-3.5" /> Top 10
            </button>
          )}
          <button
            type="button"
            onClick={() => setMode("styles")}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${mode === "styles" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Sparkles className="h-3.5 w-3.5" /> More styles
          </button>
        </div>

        {/* Personal-card style chips: Classic (brand-color) vs the dark IG looks */}
        {mode === "me" && (
          <div className="flex max-h-16 flex-wrap gap-1 overflow-y-auto">
            {ME_STYLES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setMeStyle(s.id)}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${meStyle === s.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* Team-card style chips (owners) */}
        {mode === "team" && (
          <div className="flex max-h-16 flex-wrap gap-1 overflow-y-auto">
            {TEAM_STYLES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setTeamStyle(s.id)}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${teamStyle === s.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* Legacy gallery pickers */}
        {mode === "styles" && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1">
              {galleryGroups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGroup(g.id)}
                  className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${group === g.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <div className="flex max-h-20 flex-wrap gap-1 overflow-y-auto">
              {galleryTemplates.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => setTemplate(tpl.id)}
                  className={`rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors ${template === tpl.id ? "border-primary bg-primary/10 text-primary" : "border-zinc-200 text-muted-foreground hover:text-foreground dark:border-zinc-700"}`}
                >
                  {tpl.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-[1fr_240px]">
          {/* Preview — the actual PNG the route renders. */}
          <div className="overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img key={imgPath} src={imgPath} alt="Share card preview" className="block h-auto w-full" />
          </div>

          {/* Send-to-phone QR: desktop → phone → Instagram. */}
          <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-300 p-4 text-center dark:border-zinc-600">
            {qrSrc ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={qrSrc} alt="QR code to open this card on your phone" width={160} height={160} />
            ) : null}
            <p className="flex items-center gap-1.5 text-xs font-semibold text-zinc-700 dark:text-zinc-300">
              <Smartphone className="h-3.5 w-3.5" /> Scan to open on your phone
            </p>
            <p className="text-[11px] leading-snug text-zinc-500">
              Post straight to Instagram or your story from there.
            </p>
          </div>
        </div>

        {/* Caption — visible so it can be selected manually if clipboard is denied. */}
        <p className="whitespace-pre-line rounded-lg bg-zinc-50 px-3 py-2 text-xs leading-relaxed text-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-300">
          {caption}
        </p>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" asChild className="gap-2">
            <a href={imgPath} download="insuracentral-win.png">
              <Download className="h-4 w-4" /> Download
            </a>
          </Button>
          <Button variant="outline" onClick={copyCaption} className="gap-2">
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy caption"}
          </Button>
          <Button onClick={nativeShare} disabled={sharing} className="gap-2">
            <Share2 className="h-4 w-4" />
            {sharing ? "Sharing…" : "Share"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
